import React, { useState } from 'react';
import { Table, Space, Spin, Button, message } from 'antd';
import { 
  TableWrapper, 
  ProjectAskEditModal,
  StandbyScheduleEditModal
 } from 'components/molecules';
import { usePopupManager } from 'react-popup-manager';
import { ColumnsType } from 'antd/lib/table/interface';
import { TotalNumMenu } from 'components/atoms';
import { addNewSearch } from 'utils/utils';
import { useTableInfo } from 'hooks';
import { INIT_PAGESIZE } from 'commons/constants';
import { useLocation, useNavigate } from 'react-router';
import qs from 'query-string';
import moment from 'moment';
import useStore from 'store'
import { SERVER_URL } from 'commons/constants';
import axios from 'axios';
import { endpoints } from 'services';

interface FormItemProps {
  scheduleList: ProjectScheduleDto[] | undefined;  
  searchResult: any;
  isLoading: boolean;
  refetch: () => void;
}

const StandbyScheduleTable = ({scheduleList, searchResult, refetch, isLoading}: FormItemProps): React.ReactElement => {
  /* ======================================================
   * variables
   * ====================================================== */
  const navigate = useNavigate();
  const location = useLocation();
  const { open } = usePopupManager();
  const searchParams = qs.parse(location.search);
  const [isExcelLoading, setIsExcelLoading] = useState<boolean>(false);  
  const auth = useStore((state) => state.auth); 

  const onCellClick = (record) => {
    return {
      onClick: () => {
        open(StandbyScheduleEditModal, {
          id: record.id,
          onClose: (isOk) => {refetch();},
        });     
      },
    };
   };

  const columns: ColumnsType<ProjectScheduleDto> = [
    {
      title: '상태',
      dataIndex: 'dup_count',
      key: 'dup_count',
      width: '60px',
      align: 'center',
      onCell: onCellClick,
      render: (value, record) => {
        return (
          record.dup_count>1?'대기':'논의'
        )
      }        
    },      
    {
      title: '강의일',
      dataIndex: 'date',
      key: 'date',
      width: '60px',
      align: 'center',
      onCell: onCellClick,
    },  
    {
      title: '강의시간',
      dataIndex: 'time',
      key: 'time',
      width: '70px',
      align: 'center',
      onCell: onCellClick,
    },  
    {
      title: '강사',
      dataIndex: 'lecturer',
      key: 'lecturer',
      width: '70px',
      align: 'center',
      onCell: onCellClick,
    },      
    {
      title: '일정명',
      dataIndex: 'project_title',
      key: 'project_title',
      width: '200px',
      align: 'center',
      ellipsis: true,
      onCell: onCellClick,
    },   
    {
      title: '강의구분',
      dataIndex: 'lecture_type',
      key: 'lecture_type',
      width: '120px',
      align: 'center',
      ellipsis: true,
      onCell: onCellClick,
    },        
    {
      title: '강의주제',
      dataIndex: 'lecture_subject',
      key: 'lecture_subject',
      width: '120px',
      align: 'center',
      ellipsis: true,
      onCell: onCellClick,
    },    
    {
      title: 'PM',
      dataIndex: 'pm',
      key: 'pm',
      width: '60px',
      align: 'center',
      ellipsis: true,
      onCell: onCellClick,
    },            
    {
      title: '문의',
      dataIndex: 'ask',
      key: 'ask',
      width: '80px',
      align: 'center',
      render: (_, record) => {
        return (
          record.ask_group_srl>0 && (
            <Button 
              style={{marginLeft:'5px'}} 
              type="primary"
              onClick={()=>{
                open(ProjectAskEditModal, {
                    id: record.ask_group_srl,
                }); 
              }} 
            >문의 보기</Button>
          )
        )
      } 
    },
  ];

  /* ======================================================
  * events
  * ====================================================== */ 

 const onClickRegister = () => {
    open(StandbyScheduleEditModal, {
      id: 0, 
      schedule_date: moment().format('YYYY-MM-DD'),
      schedule_start_time: '09:00',
      schedule_end_time: '10:00',
      onClose: (isOk)=>{
        if(isOk && refetch) {
          refetch();
        }
      }}
    );
 }

 const onChangePageInfo = (pageNo: number, pageSize: number) => {
   navigate({
     ...location,
     search: addNewSearch(location.search, { pageNo, pageSize }),
   });
 };  

  const onChangeSorters = (sorter: any)  => {
    const { order, field } = sorter;
    const reverse = order === 'ascend' ? 'Y' : '';

    navigate({
      ...location,
      search: addNewSearch(location.search, { pageNo: 1, order:field, reverse }),
    });  
  }; 
 
  const onNewExcelDownload = () => {
    setIsExcelLoading(true); 
    axios({
      url: SERVER_URL + endpoints.mypage.b2b4Excel,
      method: 'post',
      responseType: 'arraybuffer',
      params: {
        pageNo: 1,
        pageSize: 0,
        pm: ''+auth.login?.id,  
        group_id: 0, 
      },
    })
    .then((response) => {
      const fileName = `my_B2B프로젝트미연결.xlsx`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${fileName}`);
      link.style.cssText = 'display:none';
      document.body.appendChild(link);
      link.click();
      setIsExcelLoading(false); 
    })
    .catch(() => {
      setIsExcelLoading(false); 
      message.error('파일 다운로드 중 에러가 발생했습니다.', 1);      
    });     
  } 


  /* ======================================================
  * hooks
  * ====================================================== */
  const { pagination, onTableChange } = useTableInfo({
    pageInfo: {
      pageNo: Number(searchParams.pageNo) || 1,
      pageSize: Number(searchParams.pageSize) || INIT_PAGESIZE,
    },
    datas: scheduleList,
    totalCount: Number(searchResult.totalCount),    
    onChangePageInfo,
    onChangeSorters,
  });

  /* ======================================================
   * render items
   * ====================================================== */
  return (
    <Space direction="vertical" style={{ width: '100%'}}>
      <Spin spinning={isLoading || isExcelLoading} tip="처리중입니다. 잠시만 기다려 주세요." size="large">
        <TotalNumMenu totalNum={searchResult.totalCount} useRegister useExcelDownload onUseRegister={onClickRegister} onDownloadExcel={onNewExcelDownload}/>
        <TableWrapper isSearch={!!searchParams.search || !!searchParams.lecturer_type || !!searchParams.lecturer_category}>
          <Table
            tableLayout="fixed"
            dataSource={scheduleList}
            columns={columns}
            pagination={pagination}
            onChange={onTableChange}
            sortDirections={["descend", "ascend"]}
            style={{ width: '100%', overflowX: 'auto', minWidth: 600 }}
            className="columnCenterFixTable clickable"
            showSorterTooltip={false}
            rowKey={'id'}
          />
        </TableWrapper> 
      </Spin>
    </Space>
  );
};

export default StandbyScheduleTable;
